import { useEffect, useState } from "react";
import { FormGenerator } from "@proof/ui";
import {
  freeform,
  reusableFormSchema,
  predefinedProfileSchema,
  type Resource,
  type ProfileDatasetView,
  type PredefinedProfile,
} from "@proof/core";
import { useAuthoringServices } from "./services";
import { Button, Control, Modal } from "./components";

export function ProfileDatasetEditor({
  resource,
  onClose,
  initialRecordId = "",
  startNew = false,
}: {
  resource: Resource;
  onClose(): void;
  /** Open straight to this predefined profile (from the profiles grid). */
  initialRecordId?: string;
  /** Start with a new, unsaved profile selected. */
  startNew?: boolean;
}) {
  const services = useAuthoringServices();
  const [dataset, setDataset] = useState<ProfileDatasetView>();
  const [selected, setSelected] = useState(initialRecordId);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    let active = true;
    void services
      .profileDataset?.(resource)
      .then((value) => {
        if (!active) return;
        if (startNew) {
          const row = predefinedProfileSchema.parse({
            id: crypto.randomUUID(),
            name: `Profile ${value.records.length + 1}`,
            values: {},
            audience: {},
          });
          setDataset({ ...value, records: [...value.records, row] });
          setSelected(row.id);
        } else setDataset(value);
      })
      .catch((error) => {
        if (active) setError(error.message);
      });
    return () => {
      active = false;
    };
  }, [resource, services.profileDataset]);
  const record = dataset?.records.find((row) => row.id === selected);
  const update = (next: PredefinedProfile) => {
    setSaved(false);
    setDataset(
      (old) =>
        old && {
          ...old,
          records: old.records.map((row) => (row.id === next.id ? next : row)),
        },
    );
  };
  const fields = reusableFormSchema.parse(resource.value).fields;
  const project = {
    ...freeform,
    id: `dataset-${resource.id}`,
    tenantId: resource.tenantId,
    name: resource.name,
    fields,
    groups: [],
    tabs: [],
    parts: [],
    triggers: [],
    forms: [],
    formInstances: [],
    pages: freeform.pages.map((page) => ({ ...page, nodes: [] })),
  };
  return (
    <Modal
      title={`Predefined dataset · ${resource.name}`}
      wide
      onClose={() => {
        if (!busy) onClose();
      }}
    >
      <p>
        Create named records containing predefined values. Users can select
        authorized records to fill linked variables. Definitions stay reusable;
        record values and access lists are stored privately.
      </p>
      {error && <p role="alert">{error}</p>}
      {!services.profileDataset && (
        <p>
          Provide profileDataset and saveProfileDataset services to manage
          predefined records.
        </p>
      )}
      {dataset && (
        <fieldset
          disabled={busy}
          style={{ border: 0, padding: 0, minWidth: 0 }}
        >
          <div className="button-row">
            <Button
              onClick={() => {
                const row = predefinedProfileSchema.parse({
                  id: crypto.randomUUID(),
                  name: `Profile ${dataset.records.length + 1}`,
                  values: {},
                  audience: {},
                });
                setDataset({ ...dataset, records: [...dataset.records, row] });
                setSelected(row.id);
                setSaved(false);
              }}
            >
              Add predefined profile
            </Button>
          </div>
          <Control label="Predefined profile record">
            <select
              value={selected}
              onChange={(event) => setSelected(event.target.value)}
            >
              <option value="">Choose a record</option>
              {dataset.records.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                  {row.active ? "" : " (inactive)"}
                </option>
              ))}
            </select>
          </Control>
          {!dataset.records.length && (
            <p>
              No predefined records yet. Add a profile and enter its values and
              audience.
            </p>
          )}
          {record && (
            <section aria-label="Predefined profile editor">
              <Control label="Predefined profile name">
                <input
                  value={record.name}
                  maxLength={100}
                  onChange={(event) =>
                    update({ ...record, name: event.target.value })
                  }
                />
              </Control>
              <Control label="Active profile">
                <input
                  type="checkbox"
                  checked={record.active}
                  onChange={(event) =>
                    update({ ...record, active: event.target.checked })
                  }
                />
              </Control>
              <h3>Predefined values</h3>
              <FormGenerator
                project={project}
                values={record.values}
                onChange={(values) => update({ ...record, values })}
                assets={services.assets}
                upload={services.upload}
                transformImage={services.transformImage}
                loadAsset={services.loadAsset}
              />
              <h3>Who can select this profile?</h3>
              <p>
                User, group and company links are alternatives. When roles are
                selected, the user must also have one of those roles. With no
                links or roles, this record is unavailable. Company and group
                memberships come from the authenticated host.
              </p>
              <Control label="Available to entire workspace">
                <input
                  type="checkbox"
                  checked={record.audience.workspace}
                  onChange={(event) =>
                    update({
                      ...record,
                      audience: {
                        ...record.audience,
                        workspace: event.target.checked,
                      },
                    })
                  }
                />
              </Control>
              {(
                [
                  ["users", "User IDs"],
                  ["groups", "User group IDs"],
                  ["companies", "Company IDs"],
                ] as const
              ).map(([key, label]) => (
                <Control
                  key={key}
                  label={label}
                  note="Separate identities with commas."
                >
                  <input
                    key={`${record.id}:${key}`}
                    defaultValue={record.audience[key].join(", ")}
                    onBlur={(event) =>
                      update({
                        ...record,
                        audience: {
                          ...record.audience,
                          [key]: [
                            ...new Set(
                              event.target.value
                                .split(",")
                                .map((value) => value.trim())
                                .filter(Boolean),
                            ),
                          ],
                        },
                      })
                    }
                  />
                </Control>
              ))}
              <Control label="Allowed roles">
                <select
                  multiple
                  value={record.audience.roles}
                  onChange={(event) =>
                    update({
                      ...record,
                      audience: {
                        ...record.audience,
                        roles: Array.from(
                          event.target.selectedOptions,
                          (option) => option.value,
                        ),
                      },
                    })
                  }
                >
                  <option value="tenant.editor">Editor</option>
                  <option value="tenant.tester">Tester</option>
                  <option value="tenant.asset-manager">Asset Manager</option>
                  <option value="tenant.administrator">Administrator</option>
                </select>
              </Control>
              <p>
                No selected roles means any role within the selected audience.
              </p>
              <div className="button-row">
                <Button
                  onClick={() => {
                    const copy = {
                      ...structuredClone(record),
                      id: crypto.randomUUID(),
                      name: `${record.name} copy`.slice(0, 100),
                    };
                    setDataset({
                      ...dataset,
                      records: [...dataset.records, copy],
                    });
                    setSelected(copy.id);
                    setSaved(false);
                  }}
                >
                  Duplicate profile
                </Button>
                <Button
                  onClick={() => {
                    setDataset({
                      ...dataset,
                      records: dataset.records.filter(
                        (row) => row.id !== record.id,
                      ),
                    });
                    setSelected("");
                    setSaved(false);
                  }}
                >
                  Remove predefined profile
                </Button>
              </div>
            </section>
          )}
          {saved && (
            <p role="status">Predefined profiles and audience links saved.</p>
          )}
          <div className="button-row">
            <Button
              disabled={!services.saveProfileDataset}
              onClick={() => {
                setBusy(true);
                setError("");
                setSaved(false);
                void services.saveProfileDataset!(
                  {
                    resourceId: resource.id,
                    resourceVersion: resource.version,
                    records: dataset.records,
                    expectedRevision: dataset.revision,
                  },
                  resource,
                )
                  .then((value) => {
                    setDataset(value);
                    setSaved(true);
                  })
                  .catch((error) => setError(error.message))
                  .finally(() => setBusy(false));
              }}
            >
              Save predefined dataset
            </Button>
            <Button onClick={onClose}>Close dataset</Button>
          </div>
        </fieldset>
      )}
    </Modal>
  );
}
